import { useEffect, useState } from "react";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import { LinkContainer } from "react-router-bootstrap";
import { StructureBase } from "../lib/structures";
import { useLocation } from "react-router-dom";
import { getLocalToken } from "react-u5auth";
import DropdownButton from "react-bootstrap/DropdownButton";
import Icons from "../lib/Icons";
import useAllFeatures from "../data/useAllFeatures";
import { LoadingSpinner } from "./spinners";

interface ViewButtonsProps {
  structure: StructureBase;
  section: string;
  admin?: boolean;
  hideDownload?: boolean;
}

interface ViewButtonSections {
  id: string;
  suffix: string;
  title: string;
}

const userSections: ViewButtonSections[] = [
  { id: "summary", suffix: "", title: "Summary" },
  { id: "registration", suffix: "/registration", title: "Registration" },
  { id: "canvassing", suffix: "/canvassing", title: "Canvassing" },
  { id: "microtargeting", suffix: "/microtargeting", title: "Microtargeting" },
  { id: "issues", suffix: "/issues", title: "Issues" },
  { id: "action", suffix: "/action", title: "Action Map" },
  { id: "participation", suffix: "/participation", title: "Participation" },
  { id: "crunchweekcanvassing", suffix: "/crunchweek", title: "Crunch Week" },
  { id: "crunchweekcanvassinglead", suffix: "/crunchweek-leader", title: "Leaderboard" }
];

const adminSections: ViewButtonSections[] = [];

export default function ViewButtons({
  structure,
  section,
  admin = false,
  hideDownload = false,
}: ViewButtonsProps): JSX.Element {
  //fetch all features
  const { loading, features } = useAllFeatures();

  const allFeatures = features;

  const renderSections = admin ? adminSections : userSections;

  //width for mobile view
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  //margin for crunch week
  const sectionGap = (s: string) => {
    const testCase = "crunchweekcanvassing";

    return {
      marginLeft: s === testCase && width > 768 ? 5 : 0,
      borderTop: s === testCase && width < 768 ? "1px solid #fff" : "",
    };
  };

  //enabled features
  const featureEnabled = (feature: string) => {
    const selectedFeature = allFeatures?.find((item) => item.name === feature);

    const isEnabled = selectedFeature?.enabled;

    return isEnabled ? true : false;
  };

  const pdfRoot = (process.env.REACT_APP_API_URL ?? "").replace(
    /graphql$/,
    "pdf",
  );
  const location = useLocation();
  const token = getLocalToken() ?? "";
  const pdfUrl = `${pdfRoot}${location.pathname}?token=${token}`;

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {width < 768 ? (
        <div className="dropdown-mobi-parent">
          <DropdownButton
            menuAlign="right"
            drop="left"
            title="Menu"
            id="dropdown-menu-align-right"
            className="dropdown-mobi-layout"
          >
            <div className="my-3 d-print-none dropdown-mobi-div">
              <ButtonGroup vertical>
                {renderSections.map((s) =>
                  s.id === section && featureEnabled(s.id) ? (
                    <Button
                      key={s.id}
                      variant="primary"
                      style={sectionGap(s.id)}
                    >
                      {s.title}
                    </Button>
                  ) : featureEnabled(s.id) ? (
                    <LinkContainer
                      key={s.id}
                      to={`/${structure.type}/${structure.code}${s.suffix}`}
                      style={sectionGap(s.id)}
                    >
                      <Button variant="secondary">{s.title}</Button>
                    </LinkContainer>
                  ) : null,
                )}
              </ButtonGroup>
            </div>
            <div className="my-3 d-none d-print-block">
              <h4>{renderSections.find((s) => s.id === section)?.title}</h4>
            </div>
          </DropdownButton>
          {!hideDownload && (
            <a
              href={pdfUrl}
              className="btn btn-primary"
              style={{ margin: "0 0 0 10px" }}
            >
              <Icons name="download" height={25} width={25} />
            </a>
          )}
        </div>
      ) : (
        <>
          <div className="my-3 d-print-none">
            {!hideDownload && (
              <div style={{ float: "right" }}>
                <a
                  className="btn btn-primary"
                  style={{ display: "flex", alignItems: "center" }}
                  href={pdfUrl}
                >
                  <Icons name="download" height={20} width={20} />
                  <span style={{ marginLeft: 10 }}>Download PDF</span>
                </a>
              </div>
            )}
            <ButtonGroup vertical={width < 768 ? true : false}>
              {renderSections.map((s) =>
                s.id === section && featureEnabled(s.id) ? (
                  <Button key={s.id} variant="primary" style={sectionGap(s.id)}>
                    {s.title}
                  </Button>
                ) : featureEnabled(s.id) ? (
                  <LinkContainer
                    key={s.id}
                    to={`/${structure.type}/${structure.code}${s.suffix}`}
                    style={sectionGap(s.id)}
                  >
                    <Button variant="secondary">{s.title}</Button>
                  </LinkContainer>
                ) : null,
              )}
            </ButtonGroup>
          </div>
          <div className="my-3 d-none d-print-block">
            <h4>{renderSections.find((s) => s.id === section)?.title}</h4>
          </div>
        </>
      )}
    </>
  );
}
